import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  TextField,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Autocomplete,
  Typography,
  Paper,
  Box,
  Grid,
  Alert,
  Card,
  CardHeader,
  Tooltip,
  Chip,
  InputAdornment,
  CardContent,
} from "@mui/material";
import {
  Business,
  LocationOn,
  Person,
  Email,
  Phone,
  Language,
  Image,
  Receipt,
  AddCircle,
  RemoveCircle,
  Save,
  Settings,
  Home,
  Public,
  Lock,
  Schedule,
  Info,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import axios from "axios";
import { API_URI } from "../../constants/GlobalConstants";

const AppSettingsComp = () => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const jwttoken = userInfo.token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState({
    companyName: "",
    street: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    name: "",
    password: "",
    email: "",
    phone: "",
    taxRates: [{ type: "", value: "", taxRegistrationNumber: "" }],
    logoUrl: "",
    webAddress: "",
    timezone: "UTC",
  });

  const availableTimezones = Intl.supportedValuesOf
    ? Intl.supportedValuesOf("timeZone")
    : ["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles"];

  // Fetch existing settings when the component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API_URI}/api/app/application-settings`,
          {
            headers: { Authorization: `Bearer ${jwttoken}` },
          }
        );

        if (data.settings) {
          setIsEditMode(true);
          setInvoiceSettings({
            companyName: data.settings.companyName,
            street: data.settings.address.street,
            city: data.settings.address.city,
            province: data.settings.address.province,
            postalCode: data.settings.address.postalCode,
            country: data.settings.address.country,
            name: data.settings.contact.name,
            password: "",
            email: data.settings.contact.email,
            phone: data.settings.contact.phone,
            taxRates: data.settings.taxRates || [{ type: "", value: "", taxRegistrationNumber: "" }],
            logoUrl: data.settings.logoUrl || "",
            webAddress: data.settings.contact.webAddress || "",
            timezone: data.settings.timezone || "UTC",
          });
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        if (err.response && err.response.status === 404) {
          setIsEditMode(false);
        } else {
          setError("Failed to fetch application settings");
        }
      }
    };

    fetchSettings();
  }, [jwttoken]);

  const handleInputChange = (e) => {
    setInvoiceSettings({
      ...invoiceSettings,
      [e.target.name]: e.target.value,
    });
  };

  const handleTaxChange = (index, field, value) => {
    const updatedTaxes = invoiceSettings.taxRates.map((tax, i) =>
      i === index ? { ...tax, [field]: value } : tax
    );
    setInvoiceSettings({ ...invoiceSettings, taxRates: updatedTaxes });
  };

  const handlers = {
    addTaxField: () => {
      setInvoiceSettings({
        ...invoiceSettings,
        taxRates: [...invoiceSettings.taxRates, { type: "", value: "", taxRegistrationNumber: "" }],
      });
    },
    removeTaxField: (index) => {
      const updatedTaxes = invoiceSettings.taxRates.filter((_, i) => i !== index);
      setInvoiceSettings({ ...invoiceSettings, taxRates: updatedTaxes });
    },
    handleSubmit: async (e) => {
      e.preventDefault();
      setLoading(true);
      setError("");
      setSuccess(false);

      try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwttoken}`,
          },
        };

        if (isEditMode) {
          await axios.put(
            `${API_URI}/api/app/application-settings`,
            invoiceSettings,
            config
          );
          setSuccess(true);
        } else {
          await axios.post(
            `${API_URI}/api/app/application-settings`,
            invoiceSettings,
            config
          );
          setSuccess(true);
          setIsEditMode(true);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to save application settings");
      }
    },
  };

  const renderSectionCard = (title, icon, children, description) => (
    <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, bgcolor: "background.paper" }}>
      <CardHeader
        avatar={icon}
        title={
          <Typography variant="h6" fontWeight="bold" color="primary">
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>{children}</CardContent>
    </Card>
  );

  if (loading && !isEditMode) {
    return (
      <Container maxWidth="lg" sx={{ bgcolor: "background.default" }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            flexDirection="column"
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading application settings...
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: "background.default", borderRadius: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, bgcolor: "background.paper" }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Settings fontSize="large" color="primary" />
            <Typography variant="h4" fontWeight="bold" color="primary">
              Application Settings
            </Typography>
            {isEditMode && (
              <Chip
                icon={<CheckCircle />}
                label="Configured"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
          <Typography variant="body1" color="text.secondary">
            Configure your application's global settings including company
            information, contact details, tax rates, and system preferences.
          </Typography>
        </Box>

        {/* Status Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<Warning />}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
            Application settings {isEditMode ? "updated" : "added"} successfully!
          </Alert>
        )}

        <form onSubmit={handlers.handleSubmit}>
          {/* Company Information Section */}
          {renderSectionCard(
            "Company Information",
            <Business color="primary" />,
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Your company's legal name" placement="top">
                  <TextField
                    label="Company Name"
                    name="companyName"
                    value={invoiceSettings.companyName}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="URL to your company logo (optional)" placement="top">
                  <TextField
                    label="Company Logo URL"
                    name="logoUrl"
                    value={invoiceSettings.logoUrl}
                    onChange={handleInputChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Image />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Your company's website URL" placement="top">
                  <TextField
                    label="Company Website"
                    name="webAddress"
                    value={invoiceSettings.webAddress}
                    onChange={handleInputChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Language />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
            </Grid>,
            "Basic company information that will appear on invoices and documents"
          )}

          {/* Address Information Section */}
          {renderSectionCard(
            "Business Address",
            <LocationOn color="primary" />,
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Your business street address" placement="top">
                  <TextField
                    label="Street Address"
                    name="street"
                    value={invoiceSettings.street}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="City" placement="top">
                  <TextField
                    label="City"
                    name="city"
                    value={invoiceSettings.city}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Province/State" placement="top">
                  <TextField
                    label="Province/State"
                    name="province"
                    value={invoiceSettings.province}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Public />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="ZIP/Postal code" placement="top">
                  <TextField
                    label="Postal Code"
                    name="postalCode"
                    value={invoiceSettings.postalCode}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Country" placement="top">
                  <TextField
                    label="Country"
                    name="country"
                    value={invoiceSettings.country}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Public />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
            </Grid>,
            "Your business address information for invoices and legal documents"
          )}

          {/* Contact Information Section */}
          {renderSectionCard(
            "Contact Information",
            <Person color="primary" />,
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Primary contact person for the business" placement="top">
                  <TextField
                    label="Contact Person Name"
                    name="name"
                    value={invoiceSettings.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Business email address" placement="top">
                  <TextField
                    label="Email Address"
                    name="email"
                    value={invoiceSettings.email}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    type="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Business phone number" placement="top">
                  <TextField
                    label="Phone Number"
                    name="phone"
                    value={invoiceSettings.phone}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="App-specific password for email sending (optional)" placement="top">
                  <TextField
                    label="Email App Password"
                    name="password"
                    type="password"
                    onChange={handleInputChange}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>
              </Grid>
            </Grid>,
            "Contact details for customer communication and system notifications"
          )}

          {/* Tax Configuration Section */}
          {renderSectionCard(
            "Tax Configuration",
            <Receipt color="primary" />,
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure tax rates and their registration numbers for invoices. You can add
                multiple tax types (GST, HST, VAT, etc.) with their respective registration numbers.
              </Typography>
              {invoiceSettings.taxRates.map((tax, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <TextField
                    label="Tax Type"
                    name="type"
                    value={tax.type}
                    onChange={(e) =>
                      handleTaxChange(index, "type", e.target.value)
                    }
                    placeholder="e.g., GST, HST, VAT"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Tax Rate (%)"
                    name="value"
                    type="number"
                    value={tax.value}
                    onChange={(e) =>
                      handleTaxChange(index, "value", e.target.value)
                    }
                    placeholder="e.g., 13"
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <TextField
                    label="Tax Registration Number"
                    name="taxRegistrationNumber"
                    value={tax.taxRegistrationNumber}
                    onChange={(e) =>
                      handleTaxChange(index, "taxRegistrationNumber", e.target.value)
                    }
                    placeholder="e.g., GSTIN123456789"
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Receipt />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Add tax rate">
                    <IconButton onClick={handlers.addTaxField} color="primary">
                      <AddCircle />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove tax rate">
                    <IconButton
                      onClick={() => handlers.removeTaxField(index)}
                      color="error"
                      disabled={invoiceSettings.taxRates.length === 1}
                    >
                      <RemoveCircle />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>,
            "Set up tax rates and registration numbers for automatic calculation on invoices"
          )}

          {/* System Preferences Section */}
          {renderSectionCard(
            "System Preferences",
            <Schedule color="primary" />,
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Select your local timezone for accurate timestamps" placement="top">
                  <Autocomplete
                    options={availableTimezones}
                    value={invoiceSettings.timezone}
                    onChange={(event, newValue) =>
                      setInvoiceSettings({ ...invoiceSettings, timezone: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Timezone"
                        variant="outlined"
                        fullWidth
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <Schedule />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 2,
                    border: "1px solid #e3f2fd",
                    borderRadius: 1,
                    backgroundColor: "#f3f9ff",
                  }}
                >
                  <Info color="info" />
                  <Typography variant="body2" color="text.secondary">
                    More system preferences will be available in future updates
                  </Typography>
                </Box>
              </Grid>
            </Grid>,
            "Configure system-wide preferences and regional settings"
          )}

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
              }}
            >
              {loading
                ? "Saving..."
                : isEditMode
                ? "Update Settings"
                : "Save Settings"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AppSettingsComp;